'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import usePath from 'helpers/hooks/usePath';
import { useAccount } from 'frontastic';
import useProjectSettings from 'frontastic/hooks/useProjectSettings';
import { initIntercom } from '../../../../frontastic/lib/intercom';

const IntercomManager: React.FC = () => {
  const { projectSettings } = useProjectSettings();
  const { intercomAppId, INTERCOM_REGION } = projectSettings ?? {};
  const intercomId = intercomAppId;
  const { pathWithoutQuery } = usePath();

  const searchParams = useSearchParams();
  const hasGmsbToken = Boolean(searchParams.get('token'));
  const { account } = useAccount(!hasGmsbToken);
  const { email, addresses } = account || {};
  const [{ firstName } = {}] = addresses || [];
  const hideLauncherOnPathPrefixes = ['/designer', '/cart', '/checkout'];
  const hideDefaultLauncher = hideLauncherOnPathPrefixes.some(
    (prefix) => pathWithoutQuery === prefix || pathWithoutQuery.startsWith(`${prefix}/`),
  );
  let intercomOptions: { region?: string } = {};

  if (INTERCOM_REGION === 'EU') intercomOptions = { region: 'eu' };
  if (INTERCOM_REGION === 'CA') intercomOptions = { region: 'ca' };
  if (INTERCOM_REGION === 'NA') intercomOptions = { region: 'na' };

  const latestState = useRef({
    email,
    firstName,
    hideDefaultLauncher,
    intercomOptions,
  });

  useEffect(() => {
    latestState.current = {
      email,
      firstName,
      hideDefaultLauncher,
      intercomOptions,
    };
  }, [email, firstName, hideDefaultLauncher, intercomOptions]);

  useEffect(() => {
    if (globalThis.window === undefined || !intercomId) return;

    const intercom = (globalThis.window as any).Intercom;

    // ✅ Already booted → sync update
    if (intercom) {
      intercom('update', {
        app_id: intercomId,
        email,
        name: firstName || '',
        hide_default_launcher: hideDefaultLauncher,
      });
      if (hideDefaultLauncher) {
        // hides any open chat windows
        intercom('hide');
      }
      return;
    }

    // ✅ Deferred boot logic
    let booted = false;
    const FALLBACK_DELAY_MS = 5000;
    const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
    const listenerOptions = { once: true, passive: true };

    const win = globalThis.window as any;

    const boot = () => {
      if (booted) return;
      booted = true;
      cleanup();

      const { email, firstName, hideDefaultLauncher, intercomOptions } = latestState.current;

      initIntercom(intercomId, email, firstName || '', {
        hide_default_launcher: hideDefaultLauncher,
        ...intercomOptions,
      });
    };

    let idleId: ReturnType<typeof requestIdleCallback> | undefined;

    const cleanup = () => {
      interactionEvents.forEach((evt) => globalThis.window.removeEventListener(evt, boot));

      if (idleId !== undefined && typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleId);
      }

      clearTimeout(fallbackTimer);
    };

    interactionEvents.forEach((evt) => globalThis.window.addEventListener(evt, boot, listenerOptions));

    if (typeof win.requestIdleCallback === 'function') {
      idleId = win.requestIdleCallback(boot, { timeout: FALLBACK_DELAY_MS });
    }

    const fallbackTimer = setTimeout(boot, FALLBACK_DELAY_MS);

    return cleanup;
  }, [intercomId, email, firstName, hideDefaultLauncher]);

  return null;
};

export default IntercomManager;
